import { AccessCollection, AccessItem, CnameData, ConnectedAccountStatus, CreateImageRequest, CreateRoleRequest, CustomDomainHomepage, OrganizationPermissionLevel, OrganizationPurchaseHistory, OrganizationTeamMember, RenderGroup, TeamSettingsType, TelemetryEntry, TopicSearchResult } from '../types';
import { LitElement, css, html } from 'lit';
import { addRoleForEntity, removeRoleForEntity, updateRoleForEntity } from '../helpers/api/roles-api';
import { arrayBufferToBase64, delay, isDefined, undefinedIfEmpty } from '../helpers/utils';
import { customElement, property, query, state } from 'lit/decorators';
import { getAllRendersForOrganization, updateOrganization } from '../helpers/api/organization-api';
import { getStripeOnboardingOrDashboardLink, loadStripeStatus } from '../helpers/api/stripe-api';

import { Dialog } from '@material/mwc-dialog';
import { Navbar } from '../components/nav-bar/nav-bar';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import featuresInfo from '../helpers/state/features-info';
import { getMainPage } from '../helpers/page-name';
import { getOrCreateOrganizationReferralCode } from '../helpers/api/referral-api';
import { getOrganizationPurchaseHistory } from '../helpers/api/purchases-api';
import { getPaginatedTopicsforBackpack } from '../helpers/api/topics-api';
import { getQueryStringParameters } from '../helpers/urls';
import { ifDefined } from 'lit/directives/if-defined';
import organizationInfo from '../helpers/state/organization-info';
import subscriptionInfo from '../helpers/state/subscription-info';
import { uploadImage } from '../helpers/api/image-api';
import { validateEmail } from '../helpers/strings';
import { validateIcon } from '../helpers/tags-categories';

@customElement('team-settings-page')
export class TeamSettingsPage extends LitElement {
    static amplitude_callsite: string = 'Team Settings';



    @query('#colorPickerPrimary')
    private readonly colorPickerPrimaryElement!: HTMLInputElement;

    @query('#colorPickerSecondary')
    private readonly colorPickerSecondaryElement!: HTMLInputElement;

    @query('#navbar')
    private readonly navBar!: Navbar;

    @query('#setDefaultTemplateModal')
    setDefaultTemplateModal!: Dialog;

    @query('#payment-element')
    checkoutElement!: HTMLElement;

    @property({ type: Boolean })
    primaryColorOpened?: boolean;

    @property({ type: Boolean })
    secondaryColorOpened?: boolean;

    @property({ type: String })
    editingField?: string;

    @state()
    settingSection?: string;

    @state()
    organizationRenders: TelemetryEntry[] | undefined;

    @state()
    renderGroups: RenderGroup[] = [];

    @state()
    iconUrl: string | undefined;

    @state()
    orgTitle: string | undefined;

    @state()
    rotatePreviewCSS: string = '';

    @state()
    topics: TopicSearchResult[] = [];

    @state()
    templateTopic?: TopicSearchResult;

    @state()
    currentTemplateId: string | undefined;

    @state()
    selectTemplateDone: boolean = false;

    @state()
    loadingTopics: boolean = false;

    @property({ type: Array })
    roles: OrganizationTeamMember[] = [];

    @state()
    rolesToRender: OrganizationTeamMember[] = [];

    @state()
    filteredRoles: OrganizationTeamMember[] = [];

    @state()
    page: number = 0;

    @state()
    showPaginationBtn: boolean = false;

    @state()
    showLogoUploader: boolean = false;

    @state()
    domainEditing : 'custom_domain' | 'custom_email' | undefined;

    @state()
    renderDetailsIndex: number = -1;

    @state()
    teamPermissionLevels: Array<Record<string, string>> = isDefined(AccessCollection.organization)
        ? AccessCollection.organization
            .map((accessItem: AccessItem) => {
                return {
                    value: accessItem.value,
                    name: accessItem.name,
                };
            })
        : [];

    @state()
    guestPermissionLevel: Record<string, string> =
        {
            name: 'Guest',
            value: 'Limited'
        };

    readonly pageLength: number = 20;

    @state()
    sortParameters: any = {
        title: 0,
        pointCount: 0,
        topicViews: 0,
        lastEdited: 1,
    };

    @state()
    activeTab: string = 'Active';

    @state()
    tabsList: string[] = ['Active', 'Pending', 'Requests'];

    @state()
    stripeInfo: ConnectedAccountStatus | undefined;

    @state()
    purchaseHistory: OrganizationPurchaseHistory | undefined;

    @state()
    loadingPayments: boolean = false;

    @state()
    referralCode: string | undefined;

    @state()
    loadingReferralCode: boolean = false;

    private readonly bindFeaturesState = new StateController(this, featuresInfo);
    private readonly bindOrganizationState = new StateController(this, organizationInfo);
    private readonly bindSubscriptionState = new StateController(this, subscriptionInfo);

    teamMemberRoles = [
        OrganizationPermissionLevel.ADMINISTRATOR,
        OrganizationPermissionLevel.OWNER,
        OrganizationPermissionLevel.COLLABORATOR,
    ];

    guestRoles = [
        OrganizationPermissionLevel.LIMITED,
    ];

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        const queryString = getQueryStringParameters();
        this.settingSection = queryString.get('setting_type') ?? TeamSettingsType.General;
        this.iconUrl = organizationInfo.organization?.logo;
        this.orgTitle = organizationInfo.organization?.name;
        this.referralCode = organizationInfo.organization?.referral_code;
        this.requestUpdate();

        // Load topics progressively with pagination
        this.loadingTopics = true;
        this.topics = [];
        await this.refreshFilteredRoles();
        window.dispatchEvent(new CustomEvent('page-content-ready'));

        await getPaginatedTopicsforBackpack(
            TeamSettingsPage.amplitude_callsite,
            (batchTopics) => {
                // Filter and add each batch as it arrives
                const orgTopics = batchTopics.filter((topic) => topic?.organization_id?.$oid === organizationInfo.id?.$oid);
                this.topics = [...this.topics, ...orgTopics];

                // Update template topic if found in this batch
                const currentTemplateId = organizationInfo.organization?.default_map_template_id?.$oid;
                if (!this.templateTopic && currentTemplateId) {
                    this.templateTopic = this.topics.find((topic) => topic._id.$oid === currentTemplateId);
                }

                this.requestUpdate();
            },
            (totalCount) => {
                // All batches loaded
                this.loadingTopics = false;
                this.requestUpdate();
            }
        );
    }

    async handleChangeField(e: any, fieldName: string) {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (organizationInfo.organization === undefined) return;

            if (fieldName === 'default_map_template_id') {
                organizationInfo.organization = {
                    ...organizationInfo.organization,
                    'default_map_template_id': {
                        $oid: e.detail.value,
                    },
                };
            } else if (fieldName === 'name_logo') {
                organizationInfo.organization = {
                    ...organizationInfo.organization,
                    name: e.detail.name,
                    logo: e.detail.logo,
                };
            } else if (fieldName === 'custom_domain') {
                const value = (e.target as HTMLInputElement).value;
                if (organizationInfo.organization?.cname?.custom_url !== value) {
                    if (organizationInfo.organization?.cname) {
                        organizationInfo.organization.cname.custom_url = value;
                        organizationInfo.organization.cname.validated = false;
                    } else {
                        const cname: CnameData = {
                            custom_url: value,
                            validated: false,
                            dns_verified: false,
                            reply_to_email: organizationInfo.organization?.cname?.reply_to_email ?? undefined,
                            homepage_behavior: organizationInfo.organization?.cname?.homepage_behavior ?? CustomDomainHomepage.Login,
                            homepage_redirect: organizationInfo.organization?.cname?.homepage_redirect ?? undefined,
                        };
                        organizationInfo.organization = {
                            ...organizationInfo.organization,
                            cname,
                        };
                    };
                }
            } else if (fieldName === 'custom_email') {
                const value = (e.target as HTMLInputElement).value;
                if (organizationInfo.organization?.cname?.reply_to_email !== value) {
                    if (organizationInfo.organization?.cname) {
                        organizationInfo.organization.cname.reply_to_email = value;
                    } else {
                        const cname: CnameData = {
                            custom_url: organizationInfo.organization?.cname?.custom_url ?? undefined,
                            validated: organizationInfo.organization?.cname?.validated ?? false,
                            dns_verified: organizationInfo.organization?.cname?.dns_verified ?? false,
                            reply_to_email: value,
                            homepage_behavior: organizationInfo.organization?.cname?.homepage_behavior ?? CustomDomainHomepage.Login,
                            homepage_redirect: organizationInfo.organization?.cname?.homepage_redirect ?? undefined,
                        };
                        organizationInfo.organization = {
                            ...organizationInfo.organization,
                            cname,
                        };
                    };
                }
            }
            else {
                organizationInfo.organization = {
                    ...organizationInfo.organization,
                    [fieldName]: e.detail.value,
                };
            }

            await updateOrganization(TeamSettingsPage.amplitude_callsite, organizationInfo.organization?._id.$oid, organizationInfo.organization, {});
            await organizationInfo.fetchOrganizationInfo(organizationInfo.organization?._id.$oid);
            this.domainEditing = undefined;
        }, 'An error has occurred.  Context: could not update the organization.');
    }

    getIcon(fieldName: string) {
        const editIcon = this.editingField === fieldName ? 'done' : 'edit';
        const editIconClass = this.editingField === fieldName ? 'editIcon done' : 'editIcon editing';
        return {
            editIcon,
            editIconClass,
        };
    }

    private openColorPicker(type: string) {
        if (type === 'primary_color') {
            this.colorPickerPrimaryElement.focus();
            this.colorPickerPrimaryElement.click();
            this.primaryColorOpened = true;
            this.secondaryColorOpened = false;
        } else if (type === 'secondary_color') {
            this.colorPickerSecondaryElement.focus();
            this.colorPickerSecondaryElement.click();
            this.primaryColorOpened = false;
            this.secondaryColorOpened = true;
        }
    }

    private async submitColor(type: string) {
        let colorToSubmit;
        if (type === 'primary_color') {
            colorToSubmit = this.colorPickerPrimaryElement.value;
            // hack to close
            this.colorPickerPrimaryElement.setAttribute('type', 'text');
            this.colorPickerPrimaryElement.setAttribute('type', 'color');
        } else if (type === 'secondary_color') {
            colorToSubmit = this.colorPickerSecondaryElement.value;
            // hack to close
            this.colorPickerSecondaryElement.setAttribute('type', 'text');
            this.colorPickerSecondaryElement.setAttribute('type', 'color');
        }

        await this.handleChangeField({
            detail: {
                value: colorToSubmit,
            }
        }, type);

        this.requestUpdate();
    }

    private async submitName() {
        await this.handleChangeField({
            detail: {
                value: this.orgTitle,
            }
        }, 'name');
    }

    private async onUpdateSettingSection(e: any) {
        this.settingSection = e.detail.settingSectionInfo.settingSection;

        if (this.settingSection === TeamSettingsType.UsageAndLimits && !isDefined(this.organizationRenders)) {
            this.organizationRenders = await getAllRendersForOrganization(TeamSettingsPage.amplitude_callsite, organizationInfo.organization?._id.$oid ?? '') ?? [];
            // get day of subscription renewal or first of next month
            const firstOfNextMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);
            const subscriptionRenewalDate = organizationInfo.organization?.chargebee_subscription?.next_billing_at ? new Date(organizationInfo.organization?.chargebee_subscription?.next_billing_at ?? '') : firstOfNextMonth;
            // sum the renders for the months from renewal date to renewal date e.g. all renders from oct 15 - nov 14, sep 15 - oct 14, etc.
            const renders: RenderGroup[] = [];
            const month_turnover_date = new Date(subscriptionRenewalDate).getDate();
            this.organizationRenders.forEach(render => {
                const renderDate = new Date(render.start_time);
                const renderDay = renderDate.getDate();
                const month_adjustment = renderDay >= month_turnover_date ? 0 : -1;
                const renderMonth = renderDate.getMonth() + month_adjustment;
                const renderYear = renderDate.getFullYear();
                const renderKey = new Date(renderYear, renderMonth, month_turnover_date).toISOString();
                const renderGroup = renders.find(renderGroup => renderGroup.start_date === renderKey);
                if (isDefined(renderGroup)) {
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    renderGroup.total_renders += render.value;
                    renderGroup.renders.push(render);
                } else {
                    renders.push({
                        start_date: renderKey,
                        end_date: new Date(renderYear, renderMonth + 1, month_turnover_date).toISOString(),
                        total_renders: render.value,
                        renders: [render],
                    });
                }
            });

            // sort for most recent month first dates are in iso format
            renders.sort((a, b) => {
                return a.start_date > b.start_date ? -1 : 1;
            });
            this.renderGroups = renders;
        } else if (this.settingSection === TeamSettingsType.Payments && (!isDefined(this.stripeInfo) || !isDefined(this.purchaseHistory))) {
            await this.loadPaymentData();
        }
    }

    async onChangeIconUrl(e: CustomEvent) {
        const validatedIcon = await validateIcon(e.detail?.imageUrl);
        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }
        this.rotatePreviewCSS = '';
        this.iconUrl = e.detail?.imageUrl;

        await this.handleChangeField({
            detail: {
                name: this.orgTitle,
                logo: this.iconUrl
            }
        }, 'name_logo');

        this.showLogoUploader = false;
    }

    async onChangeImage(e: CustomEvent) {
        const imageToUpload = e.detail?.imageToUpload;
        this.rotatePreviewCSS =  e.detail?.rotatePreviewCSS;
        const imageRequest: CreateImageRequest = {
            name: imageToUpload.file.name,
            data: arrayBufferToBase64(await imageToUpload.file.arrayBuffer()),
        };
        this.iconUrl = await uploadImage(TeamSettingsPage.amplitude_callsite, imageRequest, imageToUpload.orientation);

        await this.handleChangeField({
            detail: {
                name: this.orgTitle,
                logo: this.iconUrl
            }
        }, 'name_logo');

        this.showLogoUploader = false;
    }

    private async onShowSetDefaultTemplateModal(e: CustomEvent) {
        this.setDefaultTemplateModal.open = true;
    }

    async onSelectTemplate(e: CustomEvent) {
        const currentTemplateId = e.detail.id;
        if (isDefined(currentTemplateId)) {
            await this.handleChangeField({
                detail: {
                    value: currentTemplateId
                }
            }, 'default_map_template_id');
            this.selectTemplateDone = true;
            this.templateTopic = this.topics.find((topic) => topic._id.$oid === currentTemplateId);
        }
    }

    closeTemplateModal() {
        this.selectTemplateDone = false;
        this.setDefaultTemplateModal.close();
    }

    private viewMap() {
        if (isDefined(this.templateTopic)) {
            window.open(this.templateTopic?.cc_account_admin_link, '_blank');
        }
    }

    private renderSortIcon(field: string) {
        if (this.sortParameters[field] === 0) return html`<mwc-icon></mwc-icon>`;
        if (this.sortParameters[field] === 1) return html`<mwc-icon>arrow_downward</mwc-icon>`;
        return html`<mwc-icon style="--mdc-icon-size: 12px">arrow_upward</mwc-icon>`;
    }

    private async refreshFilteredRoles(sortByField?: string) {
        const getFiledNameBySortKey = (sortKey: string, role: OrganizationTeamMember) => {
            switch (sortKey) {
                case 'name': return role?.email;
                case 'email': return role?.email;
                case 'role': return (role?.role ).OrganizationRoleType?.level;
                default: return role?.email;
            }
        };
        if (isDefined(sortByField)) {
            this.roles.sortBy((role) => getFiledNameBySortKey(sortByField, role), this.sortParameters[sortByField] === 1 ? 1 : -1);
        }

        if (isDefined(organizationInfo.id?.$oid)) {
            await organizationInfo.getOrganizationMembers();
            this.roles = organizationInfo.allMembers;
        }

        this.filteredRoles = this.roles;
        this.rolesToRender = this.filteredRoles.slice(0, (this.page + 1) * this.pageLength);
        this.showPaginationBtn = this.filteredRoles.length > ((this.page + 1) * this.pageLength);
    }

    private async changeSortDirection(field: string) {
        Object.entries(this.sortParameters).forEach(([key, value]) => {
            if (key === field) {
                this.sortParameters[field] = (Number(this.sortParameters[field]) + 1) % 3;
            } else {
                this.sortParameters[key] = 0;
            }
        });
        await this.refreshFilteredRoles(field);
    }

    private async fetchMoreRoles() {
        this.page++;
        await this.refreshFilteredRoles();
    }

    async resetCustomDomainSettings() {
        if (confirm('Are you sure you want to reset custom domain settings?')) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                await this.handleChangeField({
                    target: {
                        value: undefined
                    }
                }, 'custom_domain');
            }, 'An error has occurred.  Context: could not update the organization.');
        }
    }

    async resetReplyToEmailSettings() {
        if (confirm('Are you sure you want to reset reply-to email settings?')) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                await this.handleChangeField({
                    target: {
                        value: undefined
                    }
                }, 'custom_email');
            }, 'An error has occurred.  Context: could not update the organization.');
        }
    }

    async handleGetOrCreateReferralCode() {
        if (!organizationInfo.organization?._id.$oid) return;

        this.loadingReferralCode = true;
        try {
            const code = await getOrCreateOrganizationReferralCode(organizationInfo.organization._id.$oid);
            this.referralCode = code;
            // Also update organizationInfo so it persists
            organizationInfo.organization.referral_code = code;
        } catch (error) {
            console.error('Failed to get/create referral code:', error);
            getMainPage().errorToastElement.showToast('Failed to get referral code. Please try again or contact support.');
        } finally {
            this.loadingReferralCode = false;
        }
    }

    async copyReferralCode(): Promise<void> {
        if (this.referralCode) {
            // Create the full referral link
            const referralLink: string = `${window.location.origin}/#referral?ref=${encodeURIComponent(this.referralCode)}`;

            await navigator.clipboard.writeText(referralLink);
            getMainPage().errorToastElement.showToast('Referral link copied to clipboard!');
        }
    }

    private createReferralLink(): string {
        if (!this.referralCode) return '';
        return `${window.location.origin}/#referral?ref=${encodeURIComponent(this.referralCode)}`;
    }

    renderGeneral() {
        return html`
            <div class="innerBox">
                <!-- Name -->
                <div class="innerItem flexContainer">
                    <div class="editWrapper">
                        <span class="formLabel">Team Name and Icon</span>
                        <span> Your team icon and name will appear around Proxi. </span>
                        <div id="editNameSection" class="editSection">
                            <account-avatar size="sm" backgroundColor="${organizationInfo.organization?.primary_color ?? '--var(main-primary-color)'}" isTeam textColor="${organizationInfo.organization?.secondary_color ?? '--var(main-secondard-color)'}" .iconUrl="${this.iconUrl ?? ''}">
                                ${organizationInfo.organization?.name?.charAt(0)}
                            </account-avatar>
                            <form-input
                                id="name"
                                type="text"
                                .value="${this.orgTitle ?? ''}"
                                @change="${async (e: any) => { this.orgTitle = e.detail.value; await this.submitName(); }}"
                            >
                            </form-input>
                        </div>
                    </div>
                    <div class="colorElementWrapper">
                        <div class="editWrapper">
                            <label class="formLabel">Primary Color</label>
                        </div>
                        <div class="colorElement flexContainer alignCenter">
                            <div class="flexContainer alignCenter pickerWrapper">
                                <input
                                    id="colorPickerPrimary"
                                    class="colorPicker"
                                    value="${ifDefined(organizationInfo.organization?.primary_color)}"
                                    type="color"
                                    @input="${async () => await this.submitColor('primary_color')}"
                                />
                                <mwc-icon-button
                                    class="pickerImage"
                                    style= "background: ${organizationInfo.organization?.primary_color}"
                                    icon="colorize"
                                    @click="${() => this.openColorPicker('primary_color')}"
                                ></mwc-icon-button>
                            </div>
                        </div>
                    </div>
                    <!-- Secondary Color -->
                    <div class="colorElementWrapper">
                        <div class="editWrapper">
                            <label class="formLabel">Secondary Color</label>
                        </div>
                        <div class="colorElement flexContainer alignCenter">
                            <div class="flexContainer alignCenter pickerWrapper">
                                <input
                                    id="colorPickerSecondary"
                                    class="colorPicker"
                                    value="${ifDefined(organizationInfo.organization?.secondary_color)}"
                                    type="color"
                                    @input="${async () => await this.submitColor('secondary_color')}"
                                />
                                <mwc-icon-button
                                    class="pickerImage"
                                    icon="colorize"
                                    style="background: ${organizationInfo.organization?.secondary_color}"
                                    @click="${() => this.openColorPicker('secondary_color')}"
                                ></mwc-icon-button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="innerItem flexContainer alignCenter">
                    <span class="formLabel">Team Logo</span>
                    <div class="editWrapper"></div>
                    ${isDefined(undefinedIfEmpty(this.iconUrl)) ? html`
                        <img src="${this.iconUrl ?? ''}" class="logoPreview" alt="logo preview" style="${this.rotatePreviewCSS}">
                    ` : ''}
                    ${!this.showLogoUploader ? html`
                        <mwc-icon-button @click="${() => {this.showLogoUploader = true;}}" icon="upload"></mwc-icon-button>
                    ` : ''}
                    ${this.showLogoUploader ? html`
                        <image-upload-component
                            .imageUrl="${organizationInfo.organization?.logo ?? ''}"
                            .autoUpload=${true}
                            @retreived-autoupload-url="${this.onChangeIconUrl}"
                        ></image-upload-component>
                        <button @click="${() => {this.showLogoUploader = false;}}" class="button">Cancel</button>
                    ` : ''}
                    </div>
                </div>
                <!-- Template -->
                <div class="innerItem flexContainer">
                    <div class="editWrapper">
                        <div class="labelColumn">
                            <label class="formLabel">Map Template</label>
                            <span> The settings, colors on the selected map will be used to initiate new maps in this team. </span>
                            ${this.loadingTopics && !isDefined(this.templateTopic) ? html`
                                <loading-element></loading-element>
                            ` : ''}
                        </div>
                        <div id="editTemplateSection" class="editTemplateSection flexContainer">
                            ${isDefined(this.templateTopic) ? html`
                                <backpack-card
                                    class="topic"
                                    .topic=${this.templateTopic}
                                    ?goToDetailPage=${true}
                                    ?hideViewerType=${true}
                                    ?hideMetadata=${true}
                                ></backpack-card>
                            ` : ''}
                            <div class="buttonsWrapperVertical flexContainer">
                                ${isDefined(this.templateTopic) ? html`
                                    <form-button @click="${() => this.viewMap()}" size="lg" class="button" borderStyle="none" backgroundColor="var(--main-secondary-color)">
                                        View Map
                                    </form-button>
                                ` : ''}
                                <form-button @click="${this.onShowSetDefaultTemplateModal}" size="lg" class="button" borderStyle="none" backgroundColor="var(--main-secondary-color)">
                                    ${isDefined(this.templateTopic) ? 'Change Default Template' : 'Set Default Template'}
                                </form-button>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Referral Code -->
                <div class="innerItem flexContainer">
                    <div class="editWrapper">
                        <div class="labelColumn">
                            <label class="formLabel">Referral Link</label>
                            <span> Share your referral link with others. When they sign up and activate their subscription using your link, you'll get a $25 credit for the referral.</span>
                        </div>
                        <div class="referral-code-section">
                            ${this.loadingReferralCode ? html`
                                <loading-element></loading-element>
                            ` : isDefined(this.referralCode) ? html`
                                <div class="referral-code-display">
                                    <div class="referral-link-container">
                                        <span class="referral-code-label">Your referral code:</span>
                                        <span class="referral-code-value">${this.referralCode}</span>
                                        <span class="referral-link-label">Share this link:</span>
                                        <span class="referral-link-value">${this.createReferralLink()}</span>
                                    </div>
                                    <form-button @click="${this.copyReferralCode}" size="sm" variant="outline" class="button">
                                        <mwc-icon>content_copy</mwc-icon> Copy Link
                                    </form-button>
                                </div>
                            ` : html`
                                <form-button @click="${this.handleGetOrCreateReferralCode}" size="md" class="button" backgroundColor="var(--main-primary-color)">
                                    Generate Referral Code
                                </form-button>
                            `}
                        </div>
                    </div>
                </div>
                <mwc-dialog id="setDefaultTemplateModal" class="dialog" hideActions>
                    <modal-dialog size="md" @close="${this.closeTemplateModal}" >
                        <set-default-map-template-component
                            slot="body"
                            .topics=${this.topics}
                            .currentTopic=${this.templateTopic}
                            .selectTemplateDone=${this.selectTemplateDone}
                            modalHeight="55vh"
                            @on-select-topic="${this.onSelectTemplate}"
                            @close-modal="${this.closeTemplateModal}"
                        ></set-default-map-template-component>
                    </modal-dialog>
                </mwc-dialog>
            </div>
        `;
    }

    setActiveTab(newTab: string) {
        this.activeTab = newTab;
    }

    renderTabs() {
        return html`
            <div class="flexContainer" style="border-bottom: 1px solid var(--theme-border-color);">
                ${this.tabsList.map((tabItem) => html`
                    <div
                        class="membersTab ${this.activeTab === tabItem ? 'active' : ''}"
                        @click="${async () => this.setActiveTab(tabItem)}"
                    >
                        ${tabItem}
                    </div>
                `)}
            </div>
        `;
    }

    async updateRole(role: OrganizationTeamMember, newRoleName: string) {

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            const newRole = {
                email: role.email,
                level: {
                    OrganizationPermissionLevelType: newRoleName.toUpperCase() as OrganizationPermissionLevel
                }
            };
            await updateRoleForEntity(
                TeamSettingsPage.amplitude_callsite,
                organizationInfo?.id?.$oid ?? '',
                newRole as CreateRoleRequest
            );
            await this.refreshFilteredRoles();
        }, 'An error has occurred.  Context: could not update the organization from the server.');
    }

    async removeRole(role: OrganizationTeamMember) {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            const newRole = {
                email: role.email,
                level: {
                    OrganizationPermissionLevelType: (role?.role ).OrganizationRoleType?.level
                }
            };
            await removeRoleForEntity(
                TeamSettingsPage.amplitude_callsite,
                organizationInfo?.id?.$oid ?? '',
                newRole as CreateRoleRequest
            );
            await this.refreshFilteredRoles();
            await organizationInfo.instantiateOrganizationDataAsAuthenticatedUser();
            this.requestUpdate();
        }, 'An error has occurred.  Context: could not update the organization from the server.');
    }

    get teamMembers() {
        return this.rolesToRender.filter((role) => (this.teamMemberRoles.includes((role?.role ).OrganizationRoleType?.level)));
    }

    get guests() {
        return this.rolesToRender.filter((role) => (this.guestRoles.includes((role?.role ).OrganizationRoleType?.level)));
    }

    // adds one user as an admin and removes ones self as an admin
    async transferOwnership() {
       const newEmail = prompt('WARNING: This will remove your admin access immediately. Enter new email for the new owner of the team. Ensure it is spelled correctly.');
        if (newEmail === null) {
            return;
        }

        if (!validateEmail(newEmail)) {
            getMainPage().errorToastElement.showToastAndThrow('Invalid email format.');
        }

        if (confirm('Are you sure you want to transfer ownership of the workspace to ' + newEmail + '?' + ' You will lose your access to the workspace and its data immediately.')) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                const newRole = {
                    email: newEmail.toLowerCase(),
                    level: {
                        OrganizationPermissionLevelType: OrganizationPermissionLevel.ADMINISTRATOR
                    }
                };
                await updateRoleForEntity(
                    TeamSettingsPage.amplitude_callsite,
                    organizationInfo?.id?.$oid ?? '',
                    newRole as CreateRoleRequest
                );
                await this.refreshFilteredRoles();
            }, 'An error has occurred.  Context: could not update the organization from the server.');

            // remove self as admin
            const selfRole = {
                email: accountInfo.email.toLowerCase(),
                level: {
                    OrganizationPermissionLevelType: OrganizationPermissionLevel.ADMINISTRATOR
                }
            };

            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                await removeRoleForEntity(
                    TeamSettingsPage.amplitude_callsite,
                    organizationInfo?.id?.$oid ?? '',
                    selfRole as CreateRoleRequest
                );

                getMainPage().errorToastElement.showToast('Ownership transferred successfully. You have been removed as an admin.');
                await delay(400);

                await accountInfo.logout();

                // log out the user
            }, 'An error has occurred.  Context: could not update the organization from the server.');
        }
    }

    renderTeamMembers() {
        const teamMembersTable = html`
            ${this.teamMembers.map((role) => {
                const accessLevel = (role?.role ).OrganizationRoleType?.level.toLowerCase();
                const isCurrentUser = role.email.toLowerCase() === accountInfo.email.toLowerCase();
                const cannotEdit = (isCurrentUser && accessLevel === 'administrator');
                const isAdmin = (organizationInfo.organization?.is_administrator === true);
                const name = role.name as string;
                return html`
                    <div class="column-item">
                        <div class="tr">
                            <div class="td desktopOnly">
                                ${isCurrentUser ? html`<span><i>${isDefined(name) ? `${name} | ` : ''}${role.email}${isCurrentUser ? html`<br>(Current User)`: ''}</i></span>` : html`<span>${name ? `${name} | ` : ''}${role.email}</span>`}
                                ${role.free ? html`<span class="free-tag">Free</span>` : ''}
                            </div>
                            <div class="td desktopOnly">
                                <proxi-selectfield
                                    outlined
                                    id="accessLevel"
                                    .value="${accessLevel}"
                                    @selected="${async (e: any) => await this.updateRole(role, e.target.value)}"
                                    class="input" ?disabled=${cannotEdit || !isAdmin}>
                                    ${isDefined(accessLevel) &&
                                        this.teamPermissionLevels
                                            .map((role) =>
                                                html`
                                                    <mwc-list-item value="${role.value.toLowerCase()}" ?selected=${role.value.toLowerCase() === accessLevel}>
                                                        ${role.name}
                                                    </mwc-list-item>
                                                `
                                            )
                                    }
                                </proxi-selectfield>
                            </div>
                            <div class="td desktopOnly actionColumn">
                                ${cannotEdit || !isAdmin ? '' : html`
                                    <mwc-icon @click=${async () => await this.removeRole(role)}>delete</mwc-icon>
                                `}
                            </div>
                        </div>
                    </div>`;
            })}`;

        const guestsTable = html`
        ${this.guests.map((role) => {
            const accessLevel = (role?.role ).OrganizationRoleType?.level.toLowerCase();
            const isCurrentUser = role.email.toLowerCase() === accountInfo.email.toLowerCase();
            const cannotEdit = (isCurrentUser && accessLevel === 'administrator');
            const isAdmin = (organizationInfo.organization?.is_administrator === true);
            const name = role.name as string;
            return html`
                <div class="column-item">
                    <div class="tr">
                        <div class="td desktopOnly">
                            <span>${name ? `${name} | ` : ''}${role.email}${isCurrentUser ? html`<br>(Current User)`: ''}</span>
                        </div>
                        ${cannotEdit || !isAdmin ? '' : html`
                            <form-button size="sm" variant="outline" @click=${async () => await this.handleSubmitNewInvite(role.email, OrganizationPermissionLevel.COLLABORATOR)} class="button">Promote to Team Member</form-button>
                        `}
                        <div class="td desktopOnly actionColumn">
                            ${cannotEdit || !isAdmin ? '' : html`
                                <mwc-icon @click=${async () => await this.removeRole(role)}>delete</mwc-icon>
                            `}
                        </div>
                    </div>
                </div>`;
        })}`;
        return html`
            <div class="innerBox">
                ${organizationInfo.organization?.is_administrator === true ? html`
                    <div class="flexContainer" style="align-items: center; margin-top: 1rem; margin-bottom: 1rem">
                        <div class="labelColumn">
                            <label class="formLabel">Invite New Teammate</label>
                            <span> ${organizationInfo.chargeableMembers.length} of ${subscriptionInfo?.number_of_seats ?? 0} seat${subscriptionInfo?.number_of_seats === 1 ? '' : 's'} used.
                                    <span @click=${() => {this.settingSection = TeamSettingsType.SubscriptionAndBilling;}} style="cursor: pointer; color: var(--theme-primary-color); text-decoration: underline;">
                                        <br>Add users to your subscription
                                </span></span>
                        </div>
                        <div style="flex: 3">
                            <form-input-access
                                type="organization"
                                placeholder="User Email"
                                @submit="${async (e: any) => await this.handleSubmitNewInvite(e.detail.value, e.detail.option)}"
                                ?isOwner=${organizationInfo.organization?.is_administrator}
                            >
                                Send Invite
                            </form-input-access>
                        </div>
                    </div>
                    ${(subscriptionInfo?.number_of_seats ?? 1) === 1 || organizationInfo.numberOfAdmins === 1 ? html`
                        <span class="formLabel" style="text-decoration: underline; cursor: pointer;" @click="${async () => await this.transferOwnership()}">Transfer Owner/Admin of Workspace to Someone Else</span>
                    ` : ''}
                ` : ''}
                <div class="innerItem alignCenter justifyBetween">
                    <label class="formLabel">Workspace Members
                    </label>
                    <br>
                    <span><i>Administrator</i> is the highest level of access. They can manage and delete workspace members, billing, and all maps. <br><i>Editors</i> can manage and delete all maps, update workspace settings, but cannot add or update workspace members or billing. <br><i>Collaborators</i> can add maps and edit all maps but cannot delete them. They do not have access to workspace settings or billing.</span>

                </div>
                <div class="membersTable">
                    <div class="tr thead">
                        <div class="th desktopOnly" @click=${async () => await this.changeSortDirection('email')}>
                            User ${this.renderSortIcon('email')}
                        </div>
                        <div class="th desktopOnly" @click=${async () => await this.changeSortDirection('role')}>
                            Role ${this.renderSortIcon('role')}
                        </div>
                        <div class="th desktopOnly actionColumn"></div>
                    </div>
                    ${teamMembersTable}
                </div>
                ${this.showPaginationBtn ? html`
                <div class="paginationContainer">
                        <form-button
                            @click=${async () => await this.fetchMoreRoles()}
                            size="md"
                            variant="outline"
                            class="loadMoreButton">
                            Load More
                        </form-button>
                    </div>
                ` : ''}
                ${this.guests.length > 0 ? html`
                    <div class="innerItem alignCenter justifyBetween">
                        <label class="formLabel">Guests</label>
                        <br>
                        <span> Guests can add, edit, and delete points only on maps they are explicitly invited to. They cannot access or use paid features. If you remove them, they will lose access to the map they were added to.</span>
                    </div>

                    <div class="membersTable">
                        <div class="tr thead">
                            <div class="th desktopOnly" @click=${async () => await this.changeSortDirection('email')}>
                                Email Address ${this.renderSortIcon('email')}
                            </div>
                            <div class="th desktopOnly actionColumn"></div>
                        </div>
                        ${guestsTable}
                    </div>
                ` : ''}
            </div>
        `;
    }

    private async handleSubmitNewInvite(email: string, accessLevel: OrganizationPermissionLevel) {
        if (!validateEmail(email)) {
            getMainPage().errorToastElement.showToastAndThrow('Invalid email format.');
        }

        // check if user has subscription to add new user
        // if there is no subscription, people can add folks without limit but they will just have to pay for them later
        if (accessLevel !== OrganizationPermissionLevel.LIMITED && organizationInfo.chargeableMembers.length >= (subscriptionInfo?.number_of_seats ?? 1) && isDefined(subscriptionInfo.subscription) && !email.toLowerCase().includes('@proxi.co')) {
            getMainPage().errorToastElement.showToastAndThrow('You have reached the maximum number of seats for your subscription. Please add more seats to invite new users.');
        }

        email = email.toLowerCase().trim();
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (
                organizationInfo.organization === undefined ||
                this.roles === undefined ||
                organizationInfo.id?.$oid === undefined ||
                email === undefined
            )
                return;
            if (email === '') {
                getMainPage().errorToastElement.showToast(
                    'Email cannot be an empty string'
                );
                return;
            }
            if (!validateEmail(email)) {
                getMainPage().errorToastElement.showToast('Invalid email format.');
                return;
            }
            if (!undefinedIfEmpty(accessLevel)) {
                getMainPage().errorToastElement.showToast('Select access type.');
                return;
            }
            const new_access_level = undefinedIfEmpty(accessLevel.toUpperCase());
            if (isDefined(organizationInfo.id.$oid)) {
                if (new_access_level === 'ADMINISTRATOR') {
                    await addRoleForEntity(
                        TeamSettingsPage.amplitude_callsite,
                        organizationInfo.id.$oid,
                        {
                            email,
                            level: { OrganizationPermissionLevelType: OrganizationPermissionLevel.ADMINISTRATOR },
                        }
                    );
                } else if (new_access_level === 'OWNER') {
                    await addRoleForEntity(
                        TeamSettingsPage.amplitude_callsite,
                        organizationInfo.id.$oid,
                        {
                            email,
                            level: { OrganizationPermissionLevelType: OrganizationPermissionLevel.OWNER },
                        }
                    );
                } else if (new_access_level === 'COLLABORATOR') {
                    await addRoleForEntity(
                        TeamSettingsPage.amplitude_callsite,
                        organizationInfo.id.$oid,
                        {
                            email,
                            level: {
                                OrganizationPermissionLevelType: OrganizationPermissionLevel.COLLABORATOR,
                            },
                        }
                    );
                } else if (new_access_level === 'LIMITED') {
                    await addRoleForEntity(
                        TeamSettingsPage.amplitude_callsite,
                        organizationInfo.id.$oid,
                        {
                            email,
                            level: {
                                OrganizationPermissionLevelType: OrganizationPermissionLevel.LIMITED,
                            },
                        }
                    );
                } else {
                    getMainPage().errorToastElement.showToastAndThrow('Invalid access level');
                }
                getMainPage().errorToastElement.showToast('New access added');
                await organizationInfo.instantiateOrganizationDataAsAuthenticatedUser();
                await this.refreshFilteredRoles();
                this.requestUpdate();
            }
        }, 'An error has occurred.  Context: could not update the organization.');
        this.requestUpdate();
    }

    renderSubscriptionBilling() {
        return html`
            <subscription-cards
                @goToTeamMembers=${() => {this.settingSection = TeamSettingsType.ManageTeamMembers;this.requestUpdate();}}
                @openWorkspaceDropdown=${() => {this.dispatchEvent(new CustomEvent('open-workspace-dropdown', {bubbles: true, composed: true}));}}
            ></subscription-cards>
        `;
    }

    renderWhiteLabel() {
        return html`
            <div style="margin-bottom: 1rem; display: flex; justify-content: space-between; gap: 1rem;">
                <settings-card header="Custom Domain" maxWidth='100%' @click="${featuresInfo.customDomainEnabled ? () => {} : () => getMainPage().errorToastElement.showToast('Custom domain feature is not enabled for your workspace. Please register for a Proxi subscription and the custom domain add-on.')}">
                    <p class="info-paragraph">Set up a custom domain for your team. This will allow you to use your own domain for your team's Proxi pages. This is usually a subdomain like app.yourorganization.com or maps.yourcompany.com. After submission, Proxi will review and approve your request and provide information on how to set up your custom domain as a CNAME record on your domain or subdomain.</p>
                    ${organizationInfo.organization?.cname && isDefined(organizationInfo.organization?.cname?.custom_url) ? html`
                        Status: ${organizationInfo.organization?.cname?.validated ? 'Active' : 'Pending'} <br> <br>
                    ` : ''}
                    ${this.domainEditing === 'custom_domain' ? html`
                        <proxi-textfield
                            id="customDomain"
                            label="Custom Domain"
                            outlined
                            placeholder="maps.yourcompany.com"
                            ?disabled=${!featuresInfo.customDomainEnabled}
                            .value="${organizationInfo.organization?.cname?.custom_url ?? ''}"
                            @change="${async (e: any) => await this.handleChangeField(e, 'custom_domain')}"
                        ></proxi-textfield>
                        ${organizationInfo.organization?.cname?.custom_url ? html`
                            <form-button variant="outline" size="xs" @click="${this.resetCustomDomainSettings}">
                                Reset Custom Domain
                            </form-button>
                        ` : ''}
                    ` : html`
                        <span>${organizationInfo.organization?.cname?.custom_url ?? 'None'}
                        <mwc-icon @click="${() => {this.domainEditing = 'custom_domain'; this.requestUpdate();}}" class="edit-icon">edit</mwc-icon></span>
                    `}
                </settings-card>
                <settings-card header="Reply-To Email" maxWidth='100%' @click="${featuresInfo.customEmailEnabled ? () => {} : () => getMainPage().errorToastElement.showToast('Custom email feature is not enabled for your workspace. Please register for a Proxi subscription and the custom email add-on.')}">
                    <p class="info-paragraph"> Set up a custom reply-to email address for your team. This will forward any emails responses from your users to a custom email address.</p>
                    ${this.domainEditing === 'custom_email' ? html`
                        <proxi-textfield
                            id="replyToEmail"
                            label="Reply-To Email"
                            outlined
                            placeholder="Enter your custom email address"
                            ?disabled=${!featuresInfo.customEmailEnabled}
                            @change="${async (e: any) => await this.handleChangeField(e, 'custom_email')}"
                        ></proxi-textfield>
                        ${organizationInfo.organization?.cname?.reply_to_email ? html`
                            <form-button variant="outline" size="xs" @click="${this.resetReplyToEmailSettings}">
                                Reset Reply-To Email
                            </form-button>
                        ` : ''}
                    ` : html`
                        <span>${organizationInfo.organization?.cname?.reply_to_email ?? 'None'}
                        <mwc-icon @click="${() => {this.domainEditing = 'custom_email'; this.requestUpdate();}}" class="edit-icon">edit</mwc-icon></span>
                    `}
                </settings-card>
            </div>
        `;
    }

    renderUsageAndLimits() {
        return html`
            <div class="innerBox">
                <div class="innerItem flexContainer" style="justify-content: center;">
                    <div class="editWrapper" style="display: flex; flex-direction: column; align-items: center;">
                        <span class="formLabel">Current Usage</span>
                        <span> Renders by ${organizationInfo.organization?.chargebee_subscription ? 'subscription period' : 'month'}</span>
                        <table class="rendersTable">
                            <thead>
                                <tr>
                                    <th>Period</th>
                                    <th>Total Renders</th>
                                    <th>View Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${this.renderGroups.map((renderGroup, index) => {
                                    const startDate = new Date(renderGroup.start_date);
                                    const endDate = new Date(renderGroup.end_date);
                                    return html`
                                        <tr>
                                            <td>${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}</td>
                                            <td>${renderGroup.total_renders} renders</td>
                                            <td><mwc-icon @click="${() => {this.renderDetailsIndex === index ? this.renderDetailsIndex = -1: this.renderDetailsIndex = index;}}" class="edit-icon">${this.renderDetailsIndex === index ? 'expand_less' : 'expand_more'}</mwc-icon></td>
                                        </tr>
                                        ${this.renderDetailsIndex === index ? html`
                                            <tr>
                                                <td colspan="3">
                                                    ${this.renderUsageSubTable(renderGroup.renders, renderGroup.start_date, renderGroup.end_date)}
                                                </td>
                                            </tr>
                                        ` : ''}
                                    `;
                                })}
                            </tbody>
                        </table>
                        <br><br>
                        <span> Additional Usage Stats Coming Soon </span>
                    </div>
                </div>
            </div>
        `;
    }

    renderUsageSubTable(TelemetryEntries: TelemetryEntry[], start_date?: string, end_date?: string) {
        // group by topic (metric key) and sum the renders
        const rendersByTopic = TelemetryEntries.reduce<Record<string, number>>((acc, render) => {
            const topic = render.metric_key.$oid;
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (acc[topic]) {
                acc[topic] += render.value;
            } else {
                acc[topic] = render.value;
            }
            return acc;
        }, {});
        // sort by most renders first
        Object.entries(rendersByTopic).sort((a, b) => b[1] - a[1]);
        return html`
            <div class="innerItem flexContainer" style="justify-content: center; background-color: var(--theme-border-color); padding: 1rem; display: flex; flex-direction: column;">
                <span style="margin-top: 1rem;">Renders by Topic ${start_date ? `(${new Date(start_date).toLocaleDateString()}` : ''} - ${end_date ? `${new Date(end_date).toLocaleDateString()})` : ''}</span>
                <table class="rendersTable" style="border: 1px solid var(--theme-border-color); margin-top: 1rem;">
                    <thead>
                        <tr>
                            <th>Topic</th>
                            <th>Renders</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${Object.entries(rendersByTopic).map(([topic, renders]) => html`
                            <tr>
                                <td>${this.topics.find((t) => t._id.$oid === topic)?.name ?? 'Map'}</td>
                                <td>${renders}</td>
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>
        `;
    }

    private async loadPaymentData() {
        if (!organizationInfo.organization?._id.$oid) return;
        if (!organizationInfo.organization?.is_administrator) return;

        this.loadingPayments = true;
        try {
            // Load both Stripe info and purchase history
            const [stripeInfo, purchaseHistory] = await Promise.all([
                loadStripeStatus(organizationInfo.organization._id.$oid),
                getOrganizationPurchaseHistory(organizationInfo.organization._id.$oid)
            ]);

            this.stripeInfo = stripeInfo;
            this.purchaseHistory = purchaseHistory;
        } catch (error) {
            console.error('Failed to load payment data:', error);
            getMainPage().errorToastElement.showToast('Failed to load payment information.');
        } finally {
            this.loadingPayments = false;
        }
    }

    private async handleStripeOnboarding() {
        if (!organizationInfo.organization?._id.$oid) return;

        try {
            const result = await getStripeOnboardingOrDashboardLink(organizationInfo.organization._id.$oid);
            window.open(result.url, '_blank');
        } catch (error) {
            console.error('Failed to initiate Stripe onboarding:', error);
            getMainPage().errorToastElement.showToast('Failed to start payment account setup.');
        }
    }

    private async goToStripeDashboard() {
        if (!organizationInfo.organization?._id.$oid) return;
        window.open(`${this.stripeInfo?.dashboard_url ?? ''}`, '_blank');
    }

    renderPayments() {
        if (this.loadingPayments) {
            return html`
                <div class="innerBox">
                    <div class="loading-container">
                        <loading-element></loading-element>
                        <span>Loading payment information...</span>
                    </div>
                </div>
            `;
        }

        if (organizationInfo.organization?.is_administrator === false) {
            return html`
                <div class="innerBox">
                    <div class="innerItem flexContainer" style="justify-content: center; text-align: center; flex-direction: column; align-items: center;">
                        <span class="formLabel">Insufficient Permissions</span>
                        <span>You do not have the necessary permissions to manage payment settings. Please contact an administrator of your workspace.</span>
                    </div>
                </div>
            `;
        }

        return html`
            <div class="innerBox">
                <!-- Stripe Account Status -->
                <div class="innerItem flexContainer">
                    <div class="editWrapper">
                        <span class="formLabel">Payment Account (Beta)</span>
                        <span>Manage your payment processing and view your payment dashboard.</span>

                        <div class="stripe-status-section">
                            <div class="status-row">
                                <span class="status-label">Account Status:</span>
                                <span class="${this.getStatusBadgeClass(this.stripeInfo?.status ?? 'not_connected')}">
                                    ${this.stripeInfo?.status === 'not_connected' ? 'Not Connected' :
                                      this.stripeInfo?.status === 'awaiting_onboarding' ? 'Awaiting Onboarding' :
                                      this.stripeInfo?.status === 'payment_enabled' ? 'Active' :
                                      this.stripeInfo?.status === 'payment_disabled' ? 'Restricted' : 'Unknown'}
                                </span>
                            </div>

                            ${this.stripeInfo?.status === 'payment_enabled' ? html`
                                <div class="status-row">
                                    <span class="status-label">Payments Enabled:</span>
                                    <span class="${this.stripeInfo.charges_enabled ? 'status-badge status-success' : 'status-badge status-error'}">
                                        ${this.stripeInfo.charges_enabled ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                <div class="status-row">
                                    <span class="status-label">Payouts Enabled:</span>
                                    <span class="${this.stripeInfo.payouts_enabled ? 'status-badge status-success' : 'status-badge status-error'}">
                                        ${this.stripeInfo.payouts_enabled ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            ` : ''}
                        </div>

                        <div class="stripe-actions">
                            ${this.stripeInfo?.status === 'not_connected' ? html`
                                <form-button
                                    @click="${this.handleStripeOnboarding}"
                                    size="md"
                                    class="button"
                                    backgroundColor="var(--main-primary-color)">
                                    Connect Stripe Account
                                </form-button>
                            ` : ''}

                            ${this.stripeInfo?.dashboard_url ? html`
                                <form-button
                                    @click="${this.goToStripeDashboard}"
                                    size="md"
                                    variant="outline"
                                    class="button">
                                    View Stripe Dashboard
                                </form-button>
                            ` : ''}

                            ${this.stripeInfo?.status === 'awaiting_onboarding' || this.stripeInfo?.status === 'payment_disabled' ? html`
                                <form-button
                                    @click="${this.handleStripeOnboarding}"
                                    size="md"
                                    class="button"
                                    backgroundColor="var(--main-secondary-color)">
                                    Complete Account Setup
                                </form-button>
                            ` : ''}
                        </div>
                    </div>
                </div>
                <completed-purchases-component
                    .data=${this.purchaseHistory}
                ></completed-purchases-component>
            </div>
        `;
    }


    render() {
        return html`
            <div id="rootContainer">
                <div class="pageContainer">
                     <div class="headingContainer flexContainer alignEnd">
                        <!-- <div class="teamSettingsCover"></div> -->
                        ${undefinedIfEmpty(this.iconUrl) ? html`
                            <img src="${this.iconUrl ?? ''}" class="logoPreviewheader" alt="logo preview" style="${this.rotatePreviewCSS}">
                        ` : ''}
                        <div class="headerTitleSection">
                            <h2 class="nameHeading">${this.orgTitle ?? ''}</h2>
                        </div>
                    </div>
                    <div class="sidebar">
                        <settings-sidebar
                            type="Team"
                            .settingSection=${this.settingSection}
                            @on-select-filter-type=${this.onUpdateSettingSection}
                        >
                        </settings-sidebar>
                    </div>
                    <div class="mainContent">
                        ${this.settingSection === TeamSettingsType.General ? this.renderGeneral() : ''}
                        ${this.settingSection === TeamSettingsType.ManageTeamMembers ? this.renderTeamMembers() : ''}
                        ${this.settingSection === TeamSettingsType.SubscriptionAndBilling ? this.renderSubscriptionBilling() : ''}
                        ${this.settingSection === TeamSettingsType.WhiteLabelling ? this.renderWhiteLabel() : ''}
                        ${this.settingSection === TeamSettingsType.UsageAndLimits ? this.renderUsageAndLimits() : ''}
                        ${this.settingSection === TeamSettingsType.Payments ? this.renderPayments() : ''}
                        <error-toast id="errorToast"></error-toast>
                    </div>
                </div>
            </div>
        `;
    }

    private getStatusBadgeClass(status: string): string {
        switch (status) {
            case 'completed':
            case 'active':
                return 'status-badge status-success';
            case 'pending':
                return 'status-badge status-warning';
            case 'failed':
            case 'restricted':
                return 'status-badge status-error';
            default:
                return 'status-badge status-neutral';
        }
    }

    static get styles() {
        return css`
            #rootContainer {
                height: 100%;
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }

            .pageContainer {
                display: flex;
                width: 100%;
                flex: 1;
                padding: 0 2rem;
                box-sizing: border-box;
                align-items: stretch;
                overflow: auto;
                flex-direction: column
            }

            .mainContent {
                flex: 1 1 0%;
                width: 100%;
                box-sizing: border-box;
                position: relative;
                padding: 1rem 2rem 0rem 2rem;
            }

            .sidebar {
                max-width: 100%;
                box-sizing: border-box;
                font-size: 12px;
            }

            .edit-icon {
                color: var(--theme-input-color);
                cursor: pointer;
                --mdc-icon-size: 0.75rem;
                padding-left: -0.5rem;
                padding-top: -0.5rem;
            }

            .teamSettingsHeader {
                padding: 2rem;
                border-bottom: 1px solid var(--theme-border-color);
                justify-content: space-between;
            }

            .teamSettingsHeader .pageTitle {
                font-size: 2rem;
                margin-top: 0;
                margin-bottom: 0.5rem;
            }

            .teamSettingsHeader .actionButtons {
                gap: 1rem;
            }

            .teamSettingsCover {
                background: linear-gradient(65.95deg, #9CE4DC 23.98%, #F6FFCF 103.37%);
                border-top-left-radius: 1.5rem;
                border-top-right-radius: 1.5rem;
                width: 100%;
                height: 136px;
                position: absolute;
                left: 0;
                top: 0;
                z-index: -1;
            }

            .editTemplateSection {
                gap: 2rem;
                padding-top: 1rem;
            }

            .innerBox {
                position: relative;
            }

            .buttonsWrapper {
                width: 100%;
                padding-top: 1.5rem;
                padding-bottom: 1.5rem;
                gap: 0.75rem;
                position: sticky;
                bottom: 0;
                border-top: 1px solid var(--theme-border-color);
                background-color: white;
            }

            .buttonsWrapperVertical {
                flex-direction: column;
                gap: 0.5rem;
            }

            .membersTab {
                padding-top: 0.5rem;
                padding-bottom: 0.5rem;
                line-height: 1.5;
                color: var(--theme-lighter-text-color);
                cursor: pointer;
                flex: 1;
                text-align: center;
                font-size: 1rem;
            }

            .paginationContainer {
                display: flex;
                justify-content: center;
                margin: 1rem 0;
            }

            .loadMoreButton {
                min-width: 120px;
            }

            .membersTab.active {
                font-weight: 700;
                border-bottom: 2px solid var(--main-primary-color);
                color: var(--main-primary-color);
            }

            .free-tag {
                background-color: var(--main-secondary-color);
                color: white;
                font-size: 0.7rem;
                padding: 0.25rem;
                border-radius: 0.25rem;
                margin-left: 0.5rem;
            }

            .logoPreview {
                height: 50px;
            }

            table {
                width: 100%;
            }

            .rendersTable {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
            }

            .rendersTable tr {
                border-bottom: 1px solid var(--theme-border-color);
            }

            .rendersTable tr:last-child {
                border-bottom: none;
            }

            .rendersTable th {
                text-align: center;
                padding: 0.5rem;
            }

            .rendersTable td {
                padding: 0.5rem;
                text-align: center;
            }

            .logoPreviewheader {
                height: 70px;
            }

            backpack-card {
                max-width: 19rem;
            }

            .flexContainer {
                display: flex;
            }

            .alignCenter {
                align-items: center;
            }

            .alignEnd {
                align-items: flex-end;
            }

            .flexEnd {
                justify-content: flex-end;
            }

            .justifyBetween {
                justify-content: space-between;
            }

            @media only screen and (max-width: 550px) {
                .flexContainer {
                    flex-direction: column;
                    text-align: center;
                }
                .flexEnd {
                    justify-content: center;
                }
                #rootContainer {
                    height: auto;
                }
                .pageContainer {
                    flex-direction: column;
                    padding-left: 0;
                }
            }

            .headingContainer {
                position: relative;
                padding-bottom: 1rem;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
            }

            .headerTitleSection {
                font-size: 1rem;
                margin-top: 0;
            }

            .nameHeading {
                color: var(--main-primary-color);
                z-index: 2;
                font-size: 1rem;
                margin-bottom: 0;
            }

            .membersHeading {
                color: var(--main-primary-color);
                font-weight: 500;
                margin-top: 0.5rem;
            }

            .actionButton {
                display: flex;
                align-items: center;
                transition: color 0.3s ease;
                font-size: 1rem;
                font-weight: bold;
                cursor: pointer;
            }

            .actionButton:hover {
                color: var(--main-primary-color);
            }

            .buttonIcon {
                --mdc-icon-size: 1.25rem;
                margin-right: 0.5rem;
            }

            mwc-icon-button {
                --mdc-icon-size: 18px;
                color: var(--theme-input-color);
                border-radius: 50%;
                box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
            }

            .innerItem {
                border-top: 1px solid var(--theme-border-color);
                padding-top: 1rem;
                padding-bottom: 1rem;
                flex: 1;
                gap: 20px;
            }

            .colorElement {
                margin-top: 0.75rem;
            }

            .formLabel {
                color: var(--theme-input-color);
                font-size: 0.75rem;
                font-weight: bold;
                display: block;
            }

            .formValue {
                color: var(--theme-input-color);
                font-size: 1rem;
                word-break: break-all;
            }

            .editWrapper {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                width: 100%;
            }

            .labelColumn {
                width: 100%;
                max-width: 21rem;
                flex:1;
            }

            .editSection {
                overflow: hidden;
                padding-top: 0.5rem;
                margin-bottom: 0.5rem;
                transition: max-height 0.5s ease;
                flex: 1;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 1rem;
            }

            .editIcon.editing {
                color: var(--main-moon-color);
            }

            .editIcon.done {
                color: var(--main-success-color);
            }

            .previewImg {
                height: 2rem;
                cursor: pointer;
            }

            .colorPicker {
                opacity: 0;
                width: 0px;
                margin: 0px;
            }

            .colorSpan {
                border-radius: 50%;
                width: 1.75rem;
                height: 1.75rem;
                display: inline-block;
                box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
            }

            .submitButton {
                margin-right: 0.5rem;
            }

            .membersTable {
                border-collapse: collapse;
                padding: 0;
                width: 100%;
                box-sizing: border-box;
            }

            .discountedPrice {
                color: var(--main-invalid-color);
                font-size: 0.75rem;
                margin-left: 0.5rem;
                text-decoration: line-through;
            }

            .membersTable .column-item {
                width: 100%;
                min-width: auto;
                padding: 0;
                box-sizing: border-box;
            }

            .membersTable .thead {
                border-top-left-radius: 1rem;
                border-top-right-radius: 1rem;
                border: 1px solid var(--theme-border-color);
                display: flex;
                padding: 1rem 1.5rem;
            }

            .membersTable .th {
                flex: 1;
                cursor: pointer;
                display: flex;
                align-items: center;
            }

            .membersTable .tr {
                display: flex;
                border: 1px solid var(--theme-border-color);
                padding: 0.5rem 1.5rem;
                align-items: center;
                gap: 20px;
            }

            .long {
                flex: 2;
            }

            mwc-select {
                position: absolute;
            }

            mwc-icon {
                cursor: pointer;
            }

            .membersTable .td {
                flex: 0.75rem;
                font-size: 0.8rem;
                height: 2rem;
                display: flex;
                align-items: center;
            }

            .actionColumn {
                max-width: 2rem;
            }

            .billingCard  {
                flex: 1;
                font-size: 14px;
                max-width: 100%;
                margin: 0.5rem;
                padding: 1.5rem;
                border-radius: 0.5rem;
                border: 1px solid var(--theme-border-color);
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05),
                    0px 2px 2px -2px rgba(31, 27, 45, 0.08);
            }

            .billingCard  .cardTitle {
                font-size: 1.25rem;
                font-weight: 700;
            }

            .priceSection {
                font-size: 1rem;
            }

            .priceSection span {
                font-size: 1.5rem;
                font-weight: 500;
            }

            .visaCardWrapper {
                border-radius: 0.5rem;
                padding: 1.25rem;
                margin-top: 1.25rem;
                gap: 1rem;
                border: 1px solid var(--theme-border-color);
                border-radius: 0.5rem;
                align-items: flex-start;
                color: var(--theme-input-color);
            }

            .periodTag {
                border: 1px solid var(--theme-focus-border-color);
                border-radius: 5rem;
                display: inline-block;
                padding: 6px 1rem;
                margin-top: 0.5rem;
            }

            .membersCount {
                margin-top: 2rem;
                margin-bottom: 1rem;
                font-size: 1rem;
                padding-left: 0.75rem;
                display: flex;
                justify-content: space-between;
            }

            .visaImgWrapper {
                border: 1px solid var(--theme-border-color);
                border-radius: 0.5rem;
                padding: 0.75rem 0.5rem;
            }

            .cardButtons form-button {
                font-size: 1rem;
                font-weight: 500;
            }

            form-button.buttonOutlined {
                color: var(--theme-light-text-color);
            }

            .visaCardWrapper form-button {
                font-size: 1rem;
                font-weight: 500;
            }

            .cardCaption {
                color: var(--theme-light-text-color);
                padding-top: 0.25rem;
            }

            vaadin-accordion {
                padding: 0.25rem 0.75rem;
                border-radius: 0.5rem;
                border: 1px solid var(--theme-border-color);
            }

            .accordionItem {
                padding-top: 0.5rem;
                padding-bottom: 0.5rem;
            }

            .accordionItem:not(:last-child) {
                border-bottom: 1px solid var(--theme-border-color);
            }

            .monthlyPrice {
                color: var(--main-primary-color);
                font-weight: 500;
            }

            /* Payments section styles */
            .loading-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 2rem;
                gap: 1rem;
            }

            .stripe-status-section {
                margin-top: 1rem;
                display: flex;
                flex-direction: column;
                gap: 0.75rem;
            }

            .status-row {
                display: flex;
                align-items: center;
                gap: 1rem;
            }

            .status-label {
                font-weight: 500;
                min-width: 150px;
            }

            .status-badge {
                padding: 0.25rem 0.75rem;
                border-radius: 0.5rem;
                font-size: 0.75rem;
                font-weight: 500;
                text-transform: uppercase;
                letter-spacing: 0.05em;
            }

            .status-success {
                background-color: var(--main-success-color-20, #d4edda);
                color: var(--main-success-color, #155724);
            }

            .status-warning {
                background-color: var(--main-warning-color-20, #fff3cd);
                color: var(--main-warning-color, #856404);
            }

            .status-error {
                background-color: var(--main-invalid-color-20, #f8d7da);
                color: var(--main-invalid-color, #721c24);
            }

            .status-neutral {
                background-color: var(--theme-border-color);
                color: var(--theme-input-color);
            }

            .stripe-actions {
                margin-top: 1.5rem;
                display: flex;
                gap: 1rem;
                flex-wrap: wrap;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 3rem;
                color: var(--theme-light-text-color);
                text-align: center;
            }

            .empty-state mwc-icon {
                --mdc-icon-size: 3rem;
                margin-bottom: 1rem;
                color: var(--theme-border-color);
            }

            .empty-state span {
                font-size: 1.125rem;
                font-weight: 500;
                margin-bottom: 0.5rem;
            }

            .empty-state p {
                margin: 0;
                color: var(--theme-lighter-text-color);
            }

           .referral-code-section {
                margin-top: 1rem;
            }

            .referral-code-display {
                display: flex;
                align-items: flex-start;
                gap: 1rem;
                padding: 0.75rem 1rem;
                background-color: var(--theme-border-color);
                border-radius: 0.5rem;
            }

            .referral-link-container {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                flex: 1;
            }

            .referral-code-label,
            .referral-link-label {
                font-size: 0.75rem;
                font-weight: 600;
                color: var(--theme-input-color);
                text-transform: uppercase;
                letter-spacing: 0.05em;
            }

            .referral-code-value {
                font-size: 1rem;
                font-weight: 600;
                font-family: monospace;
                color: var(--main-primary-color);
                background-color: rgba(255, 255, 255, 0.5);
                padding: 0.25rem 0.5rem;
                border-radius: 0.25rem;
                border: 1px solid rgba(0, 0, 0, 0.1);
            }

            .referral-link-value {
                font-size: 0.9rem;
                font-weight: 500;
                font-family: monospace;
                color: var(--theme-input-color);
                background-color: rgba(255, 255, 255, 0.5);
                padding: 0.5rem;
                border-radius: 0.25rem;
                border: 1px solid rgba(0, 0, 0, 0.1);
                word-break: break-all;
                line-height: 1.4;
            }

            @media (max-width: 768px) {
                .referral-code-display {
                    flex-direction: column;
                    align-items: stretch;
                }

                .referral-link-value {
                    font-size: 0.8rem;
                }
            }

            @media (max-width: 768px) {
                .revenue-summary {
                    flex-direction: column;
                    gap: 1rem;
                }

                .transaction-header,
                .transaction-row {
                    flex-direction: column;
                    gap: 0.5rem;
                    align-items: flex-start;
                }

                .transaction-header .th,
                .transaction-row .td {
                    flex: none;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .transaction-header .th:before,
                .transaction-row .td:before {
                    content: attr(data-label);
                    font-weight: 500;
                    min-width: 80px;
                }

                .stripe-actions {
                    flex-direction: column;
                }
            }
        `;
    }
}
